/* eslint-disable */
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message
} from 'antd';
import { LoadingOutlined, ZoomOutOutlined, CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, HistoryOutlined, ClockCircleOutlined, DollarOutlined, FileTextOutlined, SyncOutlined } from '@ant-design/icons';
import type { ColumnsType, SorterResult } from 'antd/es/table/interface';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InstitutionUserService from '../../services/InstitutionUserService';

const antIcon = <LoadingOutlined style={{ fontSize: '24px', color: '#37762F' }} spin />;
const { RangePicker } = DatePicker;

interface AuditLog {
  action: string;
  created_at: string;
  created_by: string;
  record_id: string | null;
  new_data: object | null;
  old_data: object | null;
}

interface DataType {
  bailiff_refund_request_id: string;
  key: React.Key;
  bank_code: string;
  transaction_reference: string;
  debit_account_number: string;
  debit_account_name: string;
  beneficiary_account_name: string;
  beneficiary_bank_name: string;
  beneficiary_account_number: string;
  transaction_currency: string;
  amount: number;
  reason: string;
  status: string;
  bailiff_email: string;
  bailiff_phone_number: string;
  bailiff_assignee: string;
  bailiff_national_id: string;
  property_id: string;
  execution_case_number: string;
  link: string | null;
  created_at: string;
  updated_at: string;
  bidders?: {
    full_name: string;
    nid: string;
    phone_number: string;
    email: string;
  };
  bid_securities?: {
    ref_number: string;
  };
  bidder_phone_number?: string;
  auditLogs?: AuditLog[];
  attachment_url?: string;
  attachment?: string;
}

const ViewBailiffRefundDashboard = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  // Listing & Table state
  const [loading, setLoading] = useState(false);
  const [refundRequestList, setRefundRequestList] = useState<DataType[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortedInfo, setSortedInfo] = useState<SorterResult<DataType>>({});

  // Batch Approval selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [initiating, setInitiating] = useState(false);
  const [initiateModalVisible, setInitiateModalVisible] = useState(false);
  const [approvalReason, setApprovalReason] = useState('Approved and initiated by Finance');
  const [statusTab, setStatusTab] = useState('PENDING');

  const handleStatusTabChange = (key: string) => {
    setStatusTab(key);
    setSelectedRowKeys([]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Modal display states
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<DataType | null>(null);
  const [logsModalVisible, setLogsModalVisible] = useState(false);
  const [isAttachmentPreviewVisible, setIsAttachmentPreviewVisible] = useState(false);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState('');

  // Summary statistics state
  const [stats, setStats] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchStats = () => {
    setStatsLoading(true);
    InstitutionUserService.bailiffRefundRequestStats()
      .then((res: any) => {
        if (res?.status === 200 || res?.data) {
          setStats(res.data || res);
        } else {
          setStats(res);
        }
      })
      .catch((err: any) => {
        console.error('Failed to fetch stats:', err);
      })
      .finally(() => {
        setStatsLoading(false);
      });
  };

  const renderStatCard = (
    title: string,
    value: string | number,
    subtitle: string | number,
    icon: React.ReactNode,
    iconColor: string,
    iconBg: string
  ) => {
    return (
      <Card
        bordered={false}
        className="rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 w-full"
        style={{
          background: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
          height: '115px',
          display: 'flex',
          alignItems: 'center',
          padding: '16px'
        }}
        bodyStyle={{ padding: 0, width: '100%' }}
      >
        <div className="flex items-center justify-between w-full">
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider m-0" style={{ color: '#64748b' }}>{title}</p>
            <h3 className="text-gray-800 text-lg sm:text-xl md:text-2xl font-bold mt-0.5 mb-0" style={{ color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {statsLoading ? <Spin size="small" indicator={<LoadingOutlined style={{ fontSize: 18, color: '#37762F' }} spin />} /> : value}
            </h3>
            {subtitle !== undefined && subtitle !== null && subtitle !== '' && (
              <p className="text-xs font-semibold m-0 mt-0.5" style={{ color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {statsLoading ? '' : subtitle}
              </p>
            )}
          </div>
          <div
            className="text-2xl flex items-center justify-center rounded-lg p-3 flex-shrink-0"
            style={{
              color: iconColor,
              backgroundColor: iconBg,
              width: '46px',
              height: '46px',
              marginLeft: '12px'
            }}
          >
            {icon}
          </div>
        </div>
      </Card>
    );
  };

  // Fetch pending refund requests
  const fetchRefundRequests = () => {
    setLoading(true);
    InstitutionUserService.GetPendingRefundRequestListByStatusFromBailiffs(
      pagination.pageSize,
      pagination.current,
      searchText,
      fromDate,
      toDate,
      'with_link', // linkFilter is with_link by default
      statusTab
    )
      .then((res: any) => {
        if (res.status === 200) {
          setRefundRequestList(res?.data || []);
          setPagination(prev => ({
            ...prev,
            total: res?.meta?.total || 0
          }));
        } else {
          setRefundRequestList([]);
          setPagination(prev => ({ ...prev, total: 0 }));
        }
        setLoading(false);
      })
      .catch((err: any) => {
        setLoading(false);
        if (err?.message !== 'Operation canceled due to new request.') {
          message.error(err?.message || 'Failed to fetch pending bailiff refund requests');
        }
      });
  };

  useEffect(() => {
    fetchRefundRequests();
    fetchStats();
    // eslint-disable-next-line
  }, [pagination.current, pagination.pageSize, fromDate, toDate, statusTab]);

  // Debounced search trigger
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchRefundRequests();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line
  }, [searchText]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination: any, _filters: any, sorter: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
    setSortedInfo(sorter as SorterResult<DataType>);
  };

  const clearFilters = () => {
    setSearchText('');
    setFromDate('');
    setToDate('');
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
    setSelectedRowKeys([]);
  };

  // Initiate selection approval
  const handleApproveAndInitiate = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select at least one refund request to initiate.');
      return;
    }
    setInitiateModalVisible(true);
  };

  const confirmApproveAndInitiate = () => {
    setInitiating(true);
    const totalRequested = selectedRowKeys.length;
    InstitutionUserService.approveAndInitiateBailiffRefunds({
      transactionReferences: selectedRowKeys as string[],
      reason: approvalReason
    })
      .then((res: any) => {
        const succeededCount = res?.length || 0;
        
        if (succeededCount === totalRequested) {
          message.success(`All ${succeededCount} refund(s) approved and initiated successfully!`);
          setSelectedRowKeys([]);
        } else if (succeededCount > 0) {
          message.warning(`${succeededCount} of ${totalRequested} refund(s) approved and initiated successfully. ${totalRequested - succeededCount} failed.`);
          const succeededRefs = (res?.data || []).map((item: any) => item.transaction_reference);
          setSelectedRowKeys(prev => prev.filter(key => !succeededRefs.includes(key)));
        } else {
          message.error('Failed to initiate any of the selected refunds. Please check bank details or server logs.');
        }
        setInitiateModalVisible(false);
        fetchRefundRequests();
        fetchStats();
      })
      .catch((err: any) => {
        message.error(err?.message || 'Failed to initiate refunds.');
      })
      .finally(() => {
        setInitiating(false);
      });
  };

  // Helper functions for Attachment resolving/previewing
  const resolveAttachmentUrl = (req: DataType | null): string => {
    if (!req) return '';
    const attachmentUrl = typeof req.attachment_url === 'string' ? req.attachment_url.trim() : '';
    if (attachmentUrl !== '') return attachmentUrl;

    const attachment = typeof req.attachment === 'string' ? req.attachment.trim() : '';
    if (attachment === '') return '';

    if (
      attachment.startsWith('http://') ||
      attachment.startsWith('https://') ||
      attachment.startsWith('/api/') ||
      attachment.startsWith('/uploads/')
    ) {
      return attachment;
    }

    if (attachment.includes('/refund-attachments/')) return attachment;

    const fileName = attachment.split('/').pop();
    const apiBase = typeof process.env.REACT_APP_API_BASE_URL === 'string'
      ? process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '')
      : '';
    if (fileName != null && fileName !== '' && apiBase !== '') {
      return `${apiBase}/bailiff/refund-attachments/${fileName}`;
    }

    return '';
  };

  const hasSignedParams = (url: string): boolean => {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.searchParams.get('expires') !== null && parsed.searchParams.get('signature') !== null;
    } catch {
      return url.includes('expires=') && url.includes('signature=');
    }
  };

  const getAttachmentFileName = (url: string): string => {
    const withoutQuery = url.split('?')[0] ?? '';
    const fileName = withoutQuery.split('/').pop() ?? '';
    return decodeURIComponent(fileName);
  };

  const handleViewAttachment = async (req: DataType): Promise<void> => {
    const viewUrl = resolveAttachmentUrl(req);
    if (viewUrl === '') return;

    let resolvedPreviewUrl = viewUrl;
    const needsSignedUrl = resolvedPreviewUrl.includes('/refund-attachments/') && !hasSignedParams(resolvedPreviewUrl);

    if (needsSignedUrl) {
      const fileName = getAttachmentFileName(resolvedPreviewUrl);
      if (fileName === '') {
        Modal.error({ title: 'Unable to preview attachment', content: 'Attachment file name is invalid.' });
        return;
      }

      try {
        const signedLinkResponse: any = await InstitutionUserService.getSignedBailiffAttachmentUrl(fileName);
        const signedUrl = signedLinkResponse?.data?.attachment_url ?? signedLinkResponse?.attachment_url;
        if (typeof signedUrl !== 'string' || signedUrl.trim() === '') {
          Modal.error({ title: 'Unable to preview attachment', content: 'Failed to generate secure attachment link.' });
          return;
        }
        resolvedPreviewUrl = signedUrl;
      } catch (error: any) {
        const backendMessage = error?.response?.data?.message;
        Modal.error({
          title: 'Unable to preview attachment',
          content: typeof backendMessage === 'string' && backendMessage.trim() !== ''
            ? backendMessage
            : 'Failed to generate secure attachment link.'
        });
        return;
      }
    }

    setAttachmentPreviewUrl(resolvedPreviewUrl);
    setIsAttachmentPreviewVisible(true);
  };

  const formatDate = ({ dateString }: { dateString: any }) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  // Table Columns
  const columns: ColumnsType<DataType> = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      render: (_text, _record, idx) => {
        const pageNumber = pagination.current || 1;
        const pageSize = pagination.pageSize || 10;
        const globalIndex = (pageNumber - 1) * pageSize + (idx + 1);
        return <span>{globalIndex}</span>;
      }
    },
    {
      title: 'Transaction Reference',
      dataIndex: 'transaction_reference',
      key: 'transaction_reference',
      sorter: (a, b) => (a?.transaction_reference || '').localeCompare(b?.transaction_reference || ''),
      sortOrder: sortedInfo.columnKey === 'transaction_reference' ? sortedInfo.order : null,
      ellipsis: true
    },
    {
      title: 'Bailiff Assignee',
      dataIndex: 'bailiff_assignee',
      key: 'bailiff_assignee',
      sorter: (a, b) => (a?.bailiff_assignee || '').localeCompare(b?.bailiff_assignee || ''),
      sortOrder: sortedInfo.columnKey === 'bailiff_assignee' ? sortedInfo.order : null,
      ellipsis: true,
      render: (text) => (text ? text.toUpperCase() : '-')
    },
    {
      title: 'Bank Name',
      dataIndex: 'beneficiary_bank_name',
      key: 'beneficiary_bank_name',
      ellipsis: true
    },
    {
      title: 'Account Number',
      dataIndex: 'beneficiary_account_number',
      key: 'beneficiary_account_number',
      ellipsis: true
    },
    {
      title: 'Amount (RWF)',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (val) => (
        <span style={{ color: '#37762f', fontWeight: 'bold' }}>
          {parseInt(String(val || '0')).toLocaleString()}
        </span>
      ),
      sorter: (a, b) => Number(a?.amount || 0) - Number(b?.amount || 0),
      sortOrder: sortedInfo.columnKey === 'amount' ? sortedInfo.order : null
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text) => {
        const status = String(text ?? '').trim();
        const displayStatus = status || 'PENDING';
        
        const map: Record<string, { icon: React.ReactNode; label: string; description: string; bg: string; border: string; textColor: string }> = {
          PENDING: {
            icon: <ClockCircleOutlined />,
            label: 'Pending Refund',
            description: 'Awaiting initiation',
            bg: '#fffbeb',
            border: '#fde68a',
            textColor: '#b45309'
          },
          INITIATED: {
            icon: <SyncOutlined spin />,
            label: 'Initiated Refund',
            description: 'Transfer in progress',
            bg: '#eff6ff',
            border: '#bfdbfe',
            textColor: '#1d4ed8'
          },
          COMPLETED: {
            icon: <CheckCircleOutlined />,
            label: 'Completed Refund',
            description: 'Payment completed',
            bg: '#f0fdf4',
            border: '#bbf7d0',
            textColor: '#15803d'
          },
          APPROVED: {
            icon: <CheckCircleOutlined />,
            label: 'Completed Refund',
            description: 'Payment completed',
            bg: '#f0fdf4',
            border: '#bbf7d0',
            textColor: '#15803d'
          },
          "Downloaded to be Refunded": {
            icon: <CheckCircleOutlined />,
            label: 'Completed Refund',
            description: 'Payment completed',
            bg: '#f0fdf4',
            border: '#bbf7d0',
            textColor: '#15803d'
          },
          "RECEIVED REQUEST": {
            icon: <SyncOutlined />,
            label: 'Received Request',
            description: 'Received request',
            bg: '#eff6ff',
            border: '#bfdbfe',
            textColor: '#1d4ed8'
          },
          REJECTED: {
            icon: <CloseCircleOutlined />,
            label: 'Rejected',
            description: 'Request rejected',
            bg: '#fef2f2',
            border: '#fecaca',
            textColor: '#b91c1c'
          },
          FAILED: {
            icon: <CloseCircleOutlined />,
            label: 'Bank Transfer Failed',
            description: 'Refund failed at BK Bank',
            bg: '#fef2f2',
            border: '#fecaca',
            textColor: '#b91c1c'
          },
          "Bank Transfer Failed": {
            icon: <CloseCircleOutlined />,
            label: 'Bank Transfer Failed',
            description: 'Refund failed at BK Bank',
            bg: '#fef2f2',
            border: '#fecaca',
            textColor: '#b91c1c'
          }
        };

        const cfg = map[displayStatus] ?? {
          icon: null,
          label: displayStatus,
          description: '',
          bg: '#f8fafc',
          border: '#e2e8f0',
          textColor: '#475569'
        };

        return (
          <Tooltip title={cfg.description}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              borderRadius: '20px',
              background: cfg.bg,
              border: `1px solid ${cfg.border}`,
              color: cfg.textColor,
              fontWeight: 600,
              fontSize: '12px',
              whiteSpace: 'nowrap'
            }}>
              {cfg.icon}
              {cfg.label}
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_text, record) => (
        <div className="flex justify-center items-center">
          <Button
            type="link"
            icon={<EyeOutlined style={{ fontSize: '18px', color: '#1d4ed8' }} />}
            onClick={() => {
              setSelectedRequest(record);
              setDetailModalVisible(true);
            }}
          />
        </div>
      )
    }
  ];

  // Table Row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      setSelectedRowKeys(keys);
    }
  };

  // Calculate sum of selected item amounts
  const selectedTotalAmount = refundRequestList
    .filter(item => selectedRowKeys.includes(item.transaction_reference))
    .reduce((acc, curr) => acc + Number(curr.amount || 0), 0);

  return (
    <div className="w-full p-6 bg-gray-50">
      {/* Visual System Styles */}
      <style>{`
        .enhanced-card {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }
        .enhanced-card:hover {
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }
        .enhanced-button {
          border-radius: 8px;
          font-weight: 600;
          transition: all 0.2s ease;
        }
        .enhanced-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .section-header {
          background: rgba(55, 118, 47, 0.08);
          color: #111827;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
        }
        .ant-table-thead > tr > th {
          background: linear-gradient(135deg, rgba(55, 118, 47, 0.1) 0%, rgba(74, 124, 89, 0.06) 100%) !important;
          border-bottom: 2px solid #cbd5e1 !important;
          color: #374151 !important;
          font-weight: 600 !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #e5e7eb !important;
        }
        .ant-table-tbody > tr:hover > td {
          background: rgba(55, 118, 47, 0.04) !important;
        }
      `}</style>

      {/* Page Header */}
      <div className="px-5 py-4 border-b border-gray-300" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '0 0 12px 12px',
        marginBottom: '20px',
      }}>
        <div className="flex justify-between items-center">
          <div className="text-title font-semibold text-gray-800 flex items-center">
            <div style={{
              background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px',
              boxShadow: '0 4px 8px rgba(55, 118, 47, 0.3)',
            }}>
              <span style={{ fontSize: '18px' }}>⚖️</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 m-0">Bailiff Refund Approval & Initiation Dashboard</h2>
              <p className="text-gray-500 m-0 text-sm font-medium">Finance portal to verify pending bailiff refund requests and approve bank transfers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Statistics Section */}
      <Row gutter={[16, 16]} className="mb-6" style={{ display: 'flex', flexWrap: 'wrap' }}>
        <Col style={{ flex: '1 1 220px' }}>
          {renderStatCard(
            "Pending Refunds",
            stats?.pendingBailiffRefundRequestCount || 0,
            `${parseInt(String(stats?.pendingBailiffRefundRequestAmount || '0')).toLocaleString()} RWF`,
            <ClockCircleOutlined />,
            "#d97706",
            "#fef3c7"
          )}
        </Col>
        <Col style={{ flex: '1 1 220px' }}>
          {renderStatCard(
            "Initiated Refunds",
            stats?.initiatedBailiffRefundRequestCount || 0,
            `${parseInt(String(stats?.initiatedBailiffRefundRequestAmount || '0')).toLocaleString()} RWF`,
            <SyncOutlined />,
            "#2563eb",
            "#dbeafe"
          )}
        </Col>
        <Col style={{ flex: '1 1 220px' }}>
          {renderStatCard(
            "Completed Refunds",
            stats?.completedBailiffRefundRequestCount || 0,
            `${parseInt(String(stats?.totalPayments || '0')).toLocaleString()} RWF`,
            <CheckCircleOutlined />,
            "#10b981",
            "#d1fae5"
          )}
        </Col>
        <Col style={{ flex: '1 1 220px' }}>
          {renderStatCard(
            "Failed Refunds",
            stats?.failedBailiffRefundRequestCount || 0,
            `${parseInt(String(stats?.failedBailiffRefundRequestAmount || '0')).toLocaleString()} RWF`,
            <CloseCircleOutlined />,
            "#ef4444",
            "#fee2e2"
          )}
        </Col>
        <Col style={{ flex: '1 1 220px' }}>
          {renderStatCard(
            "Total Requests",
            stats?.TotalNumberOfBailiffRequestedRefund || 0,
            `${parseInt(String(stats?.amountRequestedToBeRefund || '0')).toLocaleString()} RWF`,
            <FileTextOutlined />,
            "#4f46e5",
            "#e0e7ff"
          )}
        </Col>
        <Col style={{ flex: '1 1 220px' }}>
          {renderStatCard(
            "Unrequested Bids",
            stats?.notRequestedBidders || 0,
            "Awaiting close or refund request",
            <DollarOutlined />,
            "#6b7280",
            "#f3f4f6"
          )}
        </Col>
      </Row>

      {/* Filter Section */}
      <Card className="mb-6 rounded-xl border border-gray-200 shadow-sm">
        <Form form={form} layout="vertical">
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} md={12} xl={8}>
              <Form.Item name="search" label={<span className="font-semibold text-gray-700">Quick Search</span>} style={{ marginBottom: 0 }}>
                <Input
                  size="large"
                  placeholder="Search by assignee, case, reference, or bank..."
                  prefix={<ZoomOutOutlined style={{ color: '#6b7280' }} />}
                  onChange={handleSearch}
                  value={searchText}
                  style={{ borderRadius: '8px' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={10}>
              <Form.Item name="range_picker" label={<span className="font-semibold text-gray-700">Select Date Range</span>} style={{ marginBottom: 0 }}>
                <RangePicker
                  size="large"
                  className="w-full"
                  allowClear
                  style={{ borderRadius: '8px' }}
                  onChange={(dates, dateStrings) => {
                    if (dates) {
                      setFromDate(dates[0] ? dates[0].startOf('day').toISOString() : '');
                      setToDate(dates[1] ? dates[1].endOf('day').toISOString() : '');
                    } else {
                      setFromDate('');
                      setToDate('');
                    }
                    setPagination(prev => ({ ...prev, current: 1 }));
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} xl={6}>
              <div className="flex gap-4">
                <Button
                  onClick={clearFilters}
                  className="w-full enhanced-button"
                  size="large"
                  style={{ borderRadius: '8px' }}
                >
                  Clear Filters
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Selection Stats and Bulk Approval Actions */}
      {selectedRowKeys.length > 0 && (
        <Card
          className="mb-6 rounded-xl shadow-md border-indigo-200"
          style={{ background: 'linear-gradient(90deg, #eef2ff 0%, #e0e7ff 100%)' }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <span className="p-3 bg-indigo-600 rounded-full text-white text-xl flex items-center justify-center">
                <CheckCircleOutlined />
              </span>
              <div>
                <h4 className="text-base font-bold text-gray-800 m-0">{selectedRowKeys.length} Refund Request(s) Selected</h4>
                <p className="text-sm text-gray-600 m-0 font-medium">Total selected amount: <span className="font-bold text-indigo-700">{selectedTotalAmount.toLocaleString()} RWF</span></p>
              </div>
            </div>
            <Button
              type="primary"
              size="large"
              onClick={handleApproveAndInitiate}
              className="enhanced-button"
              style={{
                background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
                border: 'none',
                height: '46px',
                padding: '0 24px',
                fontSize: '15px'
              }}
            >
              {statusTab === 'FAILED' ? 'Retry Failed Refund(s)' : 'Approve & Initiate Refund(s)'}
            </Button>
          </div>
        </Card>
      )}

      {/* Requests Table */}
      <Card className="rounded-xl border border-gray-200 shadow-sm overflow-hidden" bodyStyle={{ padding: '0px' }}>
        <Tabs
          activeKey={statusTab}
          onChange={handleStatusTabChange}
          type="card"
          tabBarStyle={{ marginBottom: 0, padding: '16px 16px 0 16px', background: '#f8fafc' }}
          items={[
            {
              key: 'PENDING',
              label: (
                <Space>
                  Pending Refunds
                  <Badge
                    count={stats?.pendingBailiffRefundRequestCount || 0}
                    overflowCount={9999}
                    style={{ backgroundColor: '#d97706', color: '#fff' }}
                  />
                </Space>
              )
            },
            {
              key: 'INITIATED',
              label: (
                <Space>
                  Initiated Refunds
                  <Badge
                    count={stats?.initiatedBailiffRefundRequestCount || 0}
                    overflowCount={9999}
                    style={{ backgroundColor: '#2563eb', color: '#fff' }}
                  />
                </Space>
              )
            },
            {
              key: 'COMPLETED',
              label: (
                <Space>
                  Completed Refunds
                  <Badge
                    count={stats?.completedBailiffRefundRequestCount || 0}
                    overflowCount={9999}
                    style={{ backgroundColor: '#10b981', color: '#fff' }}
                  />
                </Space>
              )
            },
            {
              key: 'FAILED',
              label: (
                <Space>
                  Failed Refunds
                  <Badge
                    count={stats?.failedBailiffRefundRequestCount || 0}
                    overflowCount={9999}
                    style={{ backgroundColor: '#ef4444', color: '#fff' }}
                  />
                </Space>
              )
            }
          ]}
        />
        <div className="p-4">
          <Spin spinning={loading} indicator={antIcon} tip={`Loading ${statusTab === 'PENDING' ? 'pending' : statusTab === 'INITIATED' ? 'initiated' : statusTab === 'COMPLETED' ? 'completed' : 'failed'} bailiff refunds...`}>
            <Table
              rowSelection={(statusTab === 'PENDING' || statusTab === 'FAILED') ? rowSelection : undefined}
              rowKey="transaction_reference"
              columns={columns}
              dataSource={refundRequestList}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} ${statusTab === 'PENDING' ? 'pending' : statusTab === 'INITIATED' ? 'initiated' : statusTab === 'COMPLETED' ? 'completed' : 'failed'} requests`
              }}
              onChange={handleTableChange}
              scroll={{ x: 800 }}
              rowClassName={(record, index) => (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
            />
          </Spin>
        </div>
      </Card>

      {/* Inline Detail View Modal */}
      <Modal
        title={
          <div style={{
            background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
            color: 'white',
            padding: '16px 20px',
            borderRadius: '12px 12px 0 0',
            fontSize: '18px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '-24px -24px 0 -24px',
          }}>
            <span>📄</span>
            Bailiff Refund Request Details
          </div>
        }
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)} className="enhanced-button">
            Close
          </Button>
        ]}
        width="65%"
        centered
        destroyOnClose
      >
        {selectedRequest && (
          <div className="py-4">
            {/* Beneficiary Details */}
            <div className="mb-6 p-4 rounded-xl border border-gray-200 bg-gray-50 shadow-sm">
              <div className="section-header">
                <span>💰</span>
                Beneficiary Refund Request Details
              </div>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Beneficiary Account Name</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.beneficiary_account_name || '-'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Beneficiary Bank Name</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.beneficiary_bank_name || '-'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Beneficiary Account Number</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.beneficiary_account_number || '-'}</div>
                </Col>
              </Row>
              <Row gutter={[16, 16]} className="pt-4">
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Debit Account Number</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.debit_account_number || '-'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Debit Account Name</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.debit_account_name || '-'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Transaction Reference</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.transaction_reference || '-'}</div>
                </Col>
              </Row>
              <Row gutter={[16, 16]} className="pt-4">
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Amount to be Refunded</div>
                  <div className="text-base font-bold text-green-700">{parseInt(String(selectedRequest.amount || 0)).toLocaleString()} RWF</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Transaction Currency</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.transaction_currency || 'RWF'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Requested Date</div>
                  <div className="text-base font-medium text-gray-800">{formatDate({ dateString: selectedRequest.created_at })}</div>
                </Col>
              </Row>
            </div>

            {/* Bailiff Details */}
            <div className="mb-6 p-4 rounded-xl border border-gray-200 bg-gray-50 shadow-sm">
              <div className="section-header">
                <span>👤</span>
                Bailiff Request Details
              </div>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Bailiff Assignee</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.bailiff_assignee || '-'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Bailiff National ID</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.bailiff_national_id || '-'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Bailiff Phone Number</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.bailiff_phone_number || '-'}</div>
                </Col>
              </Row>
              <Row gutter={[16, 16]} className="pt-4">
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Bailiff Email</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.bailiff_email || '-'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Property ID</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.property_id || '-'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Execution Case Number</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.execution_case_number || '-'}</div>
                </Col>
              </Row>
              <Row gutter={[16, 16]} className="pt-4">
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-700 text-xs">Original Bidder Phone</div>
                  <div className="text-base font-medium text-gray-800">{selectedRequest.bidder_phone_number || '-'}</div>
                </Col>
              </Row>
            </div>

            {/* Document / Logs triggers */}
            <div className="flex gap-4">
              <Button
                onClick={() => handleViewAttachment(selectedRequest)}
                disabled={resolveAttachmentUrl(selectedRequest) === ''}
                className="enhanced-button"
                style={{
                  background: resolveAttachmentUrl(selectedRequest) !== '' ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' : '#9ca3af',
                  color: 'white',
                  border: 'none'
                }}
              >
                View Attachment File
              </Button>
              <Button
                icon={<HistoryOutlined />}
                onClick={() => setLogsModalVisible(true)}
                className="enhanced-button"
              >
                View Request History & Audit Logs
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* History & Logs Modal */}
      <Modal
        title={
          <div style={{
            background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
            color: 'white',
            padding: '16px 20px',
            borderRadius: '12px 12px 0 0',
            fontSize: '18px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '-24px -24px 0 -24px',
          }}>
            <span>📋</span>
            Refund Request Logs
          </div>
        }
        visible={logsModalVisible}
        onCancel={() => setLogsModalVisible(false)}
        footer={null}
        width="60%"
        centered
        destroyOnClose
      >
        <div style={{ maxHeight: '450px', overflowY: 'auto', padding: '10px 0' }}>
          {selectedRequest?.auditLogs && selectedRequest.auditLogs.length > 0 ? (
            selectedRequest.auditLogs.map((log, index) => (
              <div key={index} className="p-4 mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                <Row gutter={[16, 16]}>
                  <Col xs={12} md={6}>
                    <div className="font-semibold text-gray-500 text-xs">Action</div>
                    <div className="text-sm font-medium text-gray-800 p-2 bg-gray-50 rounded mt-1">{log.action}</div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div className="font-semibold text-gray-500 text-xs">Created By</div>
                    <div className="text-sm font-medium text-gray-800 p-2 bg-gray-50 rounded mt-1">{log.created_by}</div>
                  </Col>
                  <Col xs={24} md={12}>
                    <div className="font-semibold text-gray-500 text-xs">Created At</div>
                    <div className="text-sm font-medium text-gray-800 p-2 bg-gray-50 rounded mt-1">{formatDate({ dateString: log.created_at })}</div>
                  </Col>
                </Row>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-400 font-medium">No audit logs available for this request.</div>
          )}
        </div>
      </Modal>

      {/* Confirmation & Reason Input Modal */}
      <Modal
        title={
          <div style={{
            background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
            color: 'white',
            padding: '16px 20px',
            borderRadius: '12px 12px 0 0',
            fontSize: '18px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '-24px -24px 0 -24px',
          }}>
            <span>🔒</span>
            Approve & Initiate Refunds
          </div>
        }
        visible={initiateModalVisible}
        onOk={confirmApproveAndInitiate}
        onCancel={() => setInitiateModalVisible(false)}
        confirmLoading={initiating}
        okText="Yes, Approve & Initiate"
        cancelText="Cancel"
        centered
        destroyOnClose
      >
        <div className="py-4">
          <div className="p-4 mb-4 rounded-xl border border-indigo-200 bg-indigo-50 font-semibold text-indigo-800 text-sm">
            You are about to approve and initiate refunds for {selectedRowKeys.length} selected request(s). Total transfer: {selectedTotalAmount.toLocaleString()} RWF.
          </div>
          <Form layout="vertical">
            <Form.Item label={<span className="font-semibold text-gray-700">Approval / Initiation Reason</span>} required>
              <Input.TextArea
                rows={4}
                value={approvalReason}
                onChange={(e) => setApprovalReason(e.target.value)}
                placeholder="Enter justification message for this batch update..."
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>

      {/* Attachment Preview Modal */}
      <Modal
        visible={isAttachmentPreviewVisible}
        onCancel={() => { setIsAttachmentPreviewVisible(false); }}
        footer={null}
        width="85%"
        centered
        title="Attachment Preview"
        destroyOnClose
      >
        {attachmentPreviewUrl !== '' ? (
          <iframe
            src={attachmentPreviewUrl}
            title="Refund attachment preview"
            style={{
              width: '100%',
              height: '75vh',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }}
          />
        ) : (
          <div className="text-base font-medium text-gray-500 py-8 text-center">No preview available.</div>
        )}
      </Modal>
    </div>
  );
};

export default ViewBailiffRefundDashboard;
